import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Globe2, Eye, EyeOff, Loader2, Sparkles, ShieldCheck, Zap, BadgeCheck, ScanLine, Users, Handshake, TrendingUp, UserCheck, CheckCircle2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";

const SCAN_HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/315f67a0-df9f-4f8c-bd82-001e5f5d1a6a/images/81b8a3c298937b6546d4a4ce5ca394ed64b6453a04c89fc64228f381353d006e.jpeg";

function guessCompany(url) {
  try {
    const host = new URL(url.startsWith("http") ? url : `https://${url}`).hostname.replace(/^www\./, "");
    const name = host.split(".")[0].replace(/[-_]/g, " ");
    return name.replace(/\b\w/g, (c) => c.toUpperCase());
  } catch {
    return "";
  }
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get("ref") || "";
  const [referrerName, setReferrerName] = useState("");
  useEffect(() => {
    if (!referralCode) return;
    api.get(`/ref/${referralCode}/info`).then((r) => setReferrerName(r.data.partner_name || "")).catch(() => {});
  }, [referralCode]);
  const [step, setStep] = useState(1);
  const [url, setUrl] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", company: "", password: "" });
  const [agree, setAgree] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [seconds, setSeconds] = useState(300);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);

  const analyze = async () => {
    const value = url.trim();
    if (!value) return;
    setAnalyzing(true);
    setAnalysis(null);
    try {
      const { data } = await api.post("/ai/search", { query: `Analyze company website ${value}` });
      setAnalysis(data.structured);
    } catch (e) {
      setAnalysis(null);
    } finally {
      setAnalyzing(false);
    }
    if (!form.company) setForm((f) => ({ ...f, company: guessCompany(value) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) { setError("Please accept the Terms & Conditions to continue."); return; }
    setError("");
    setLoading(true);
    const res = await register({ ...form, referral_code: referralCode });
    setLoading(false);
    if (res.ok) { setError(""); setSeconds(300); setStep(3); }
    else setError(res.error);
  };

  useEffect(() => {
    if (step !== 3 || seconds <= 0) return;
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [step, seconds]);

  const verify = async (e) => {
    e.preventDefault();
    setVerifying(true); setError("");
    try {
      await api.post("/auth/verify-registration", { email: form.email, otp });
      navigate("/dashboard");
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setVerifying(false); }
  };

  const resend = async () => {
    setResending(true); setError("");
    try {
      await api.post("/auth/resend-otp", { email: form.email });
      setSeconds(300); setOtp("");
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setResending(false); }
  };

  return (
    <div className="relative overflow-hidden" data-testid="register-page">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(37,99,235,0.14),transparent_60%)]" />
      {/* HERO — ONE SCAN. INSTANT CONNECTION. */}
      <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-14 md:pt-20 pb-8 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <p className="gold-kicker mb-4">Connect • Collaborate • Grow</p>
          <h1 className="font-display font-black tracking-tight leading-[1.02]" data-testid="register-hero-headline">
            <span className="block text-slate-100 text-4xl sm:text-5xl lg:text-6xl">ONE SCAN.</span>
            <span className="block text-gold text-4xl sm:text-5xl lg:text-6xl mt-1">INSTANT CONNECTION.</span>
          </h1>
          <p className="text-sm md:text-base text-slate-400 mt-5 max-w-md leading-relaxed">Scan your visiting card and register in 1 minute — or enter your details below and we build your business profile.</p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Link to="/scan" data-testid="hero-scan-register-btn" className="btn-gold !px-6 !py-3.5 text-sm"><ScanLine size={16} /> SCAN CARD & REGISTER</Link>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-9 max-w-md" data-testid="register-trio">
            {[[Users, "Connect", "with verified professionals"], [Handshake, "Collaborate", "and build stronger ties"], [TrendingUp, "Grow", "your business together"]].map(([Icon, t, d]) => (
              <div key={t} className="text-center">
                <Icon size={22} className="text-techblue-light mx-auto mb-2" />
                <p className="text-xs font-bold text-slate-100">{t}</p>
                <p className="text-[10.5px] text-slate-500 mt-1 leading-snug">{d}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="hidden lg:block">
          <img src={SCAN_HERO_IMG} alt="Scan a visiting card to register" className="w-full max-w-md mx-auto rounded-sm border border-slate-800" data-testid="register-scan-hero-img" />
        </div>
      </div>

      <div className="relative max-w-xl mx-auto px-4 pb-16 md:pb-24">

        {referrerName && (
          <div className="mb-5 flex items-center gap-2.5 card-surface hairline-gold px-4 py-3 animate-fade-up" data-testid="referred-by-banner">
            <UserCheck size={15} className="text-gold shrink-0" />
            <p className="text-xs text-slate-300">Referred by <span className="text-gold font-semibold" data-testid="referred-by-name">{referrerName}</span> — 10XGLOBAL Verified Partner</p>
          </div>
        )}
        {step === 1 && (
          <div className="card-surface hairline-gold p-7" data-testid="website-step">
            <div className="flex items-center gap-2 mb-5">
              <span className="w-6 h-6 rounded-full bg-gold text-navy-950 font-mono text-xs font-bold flex items-center justify-center">1</span>
              <p className="text-xs font-mono uppercase tracking-widest text-slate-300">Your Company Website</p>
            </div>
            <div className="relative">
              <Globe2 size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
              <input value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && analyze()}
                placeholder="https://yourcompany.com" data-testid="register-website-input" className="input-dark !pl-10" />
            </div>
            <button onClick={analyze} disabled={analyzing || !url.trim()} data-testid="analyze-website-btn"
              className="btn-gold w-full mt-4 disabled:opacity-60">
              {analyzing ? <><Loader2 size={15} className="animate-spin" /> AI IS ANALYZING…</> : <><Sparkles size={15} /> ANALYZE MY WEBSITE</>}
            </button>

            {analysis && (
              <div className="mt-5 pt-5 border-t border-slate-800 animate-fade-up" data-testid="analysis-result">
                <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2.5">AI detected</p>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-navy-800 border border-gold/40 rounded-sm px-2.5 py-1 text-xs text-gold">{analysis.industry}</span>
                  <span className="bg-navy-800 border border-techblue/40 rounded-sm px-2.5 py-1 text-xs text-techblue-light">{analysis.location}</span>
                  <span className="bg-navy-800 border border-slate-700 rounded-sm px-2.5 py-1 text-xs text-slate-300">{analysis.intent}</span>
                </div>
                <button onClick={() => setStep(2)} data-testid="continue-to-details-btn" className="btn-gold w-full mt-5">
                  CONTINUE — CLAIM THIS PROFILE <ArrowRight size={15} />
                </button>
              </div>
            )}

            <button onClick={() => setStep(2)} data-testid="skip-website-btn"
              className="w-full text-center text-xs text-slate-500 hover:text-gold transition-colors mt-5">
              No website? Enter your details manually →
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="card-surface hairline-gold p-7 animate-fade-up" data-testid="details-step">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-gold text-navy-950 font-mono text-xs font-bold flex items-center justify-center">2</span>
                <p className="text-xs font-mono uppercase tracking-widest text-slate-300">Your Details</p>
              </div>
              <button onClick={() => setStep(1)} data-testid="back-to-website-btn" className="text-xs text-slate-500 hover:text-gold flex items-center gap-1"><ArrowLeft size={12} /> Back</button>
            </div>
            <form onSubmit={submit} className="space-y-4" data-testid="register-form">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Full Name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your full name" data-testid="register-name-input" className="input-dark" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Work Email</label>
                <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@company.com" data-testid="register-email-input" className="input-dark" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Company Name</label>
                <input required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                  placeholder="Your company name" data-testid="register-company-input" className="input-dark" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Password</label>
                <div className="relative">
                  <input type={showPw ? "text" : "password"} required minLength={8} value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min. 8 characters"
                    data-testid="register-password-input" className="input-dark pr-11" />
                  <button type="button" onClick={() => setShowPw(!showPw)} data-testid="register-password-toggle"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <label className="flex items-start gap-2.5 text-xs text-slate-400 cursor-pointer">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="register-terms-checkbox" className="accent-[#D4AF37] mt-0.5" />
                I agree to the Terms & Conditions and Privacy Policy
              </label>
              {error && <p className="text-sm text-red-400" data-testid="register-error">{error}</p>}
              <button type="submit" disabled={loading} data-testid="register-submit-btn" className="btn-gold w-full disabled:opacity-60">
                {loading ? "CREATING YOUR PROFILE…" : <>CREATE MY BUSINESS PROFILE <ArrowRight size={16} /></>}
              </button>
            </form>
            <p className="text-center text-xs text-slate-500 mt-4">Already a member? <Link to="/login" className="text-gold font-semibold" data-testid="register-login-link">Login</Link></p>
          </div>
        )}

        {step === 3 && (
          <div className="card-surface hairline-gold p-7 animate-fade-up" data-testid="verify-step">
            <div className="flex items-center gap-2 mb-5">
              <span className="w-6 h-6 rounded-full bg-gold text-navy-950 font-mono text-xs font-bold flex items-center justify-center">3</span>
              <p className="text-xs font-mono uppercase tracking-widest text-slate-300">Verify Your Account</p>
            </div>
            <p className="text-sm text-slate-400">Enter the 6-digit code sent to <span className="text-gold font-semibold" data-testid="verify-email-label">{form.email}</span></p>
            <form onSubmit={verify} className="space-y-4 mt-5" data-testid="verify-form">
              <input required inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                placeholder="••••••" data-testid="verify-otp-input"
                className="input-dark text-center !text-2xl font-mono tracking-[0.5em]" />
              <p className={`text-center text-[11px] font-mono ${seconds > 0 ? "text-slate-500" : "text-red-400"}`} data-testid="verify-countdown">
                {seconds > 0 ? `Code expires in ${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}` : "Code expired — resend a new one"}
              </p>
              {error && <p className="text-sm text-red-400" data-testid="verify-error">{error}</p>}
              <button type="submit" disabled={verifying || otp.length !== 6} data-testid="verify-code-btn" className="btn-gold w-full disabled:opacity-60">
                {verifying ? <Loader2 size={15} className="animate-spin" /> : <>VERIFY CODE <ShieldCheck size={16} /></>}
              </button>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Didn't receive the code?</span>
                <button type="button" onClick={resend} disabled={resending || seconds > 240} data-testid="verify-resend-btn"
                  className="text-gold font-semibold disabled:opacity-50 inline-flex items-center gap-1">
                  {resending ? <Loader2 size={12} className="animate-spin" /> : "Resend"}
                </button>
              </div>
            </form>
            <div className="mt-5 pt-4 border-t border-slate-800">
              <p className="text-center text-[11px] text-slate-600 mb-3">Your account is already created and active — verification is optional.</p>
              <button type="button" onClick={() => navigate("/dashboard")} data-testid="verify-skip-btn" className="btn-outline w-full">CONTINUE WITHOUT OTP — VERIFY LATER</button>
            </div>
          </div>
        )}

        <div className="mt-10" data-testid="register-how-it-works">
          <p className="text-center text-xs font-mono uppercase tracking-[0.3em] text-gold mb-6">How It Works</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[[ScanLine, "SCAN CARD", "Scan any visiting card instantly."], [Sparkles, "AUTO EXTRACT", "AI captures and fills the details accurately."], [UserCheck, "CREATE PROFILE", "Review the details and create your profile."], [CheckCircle2, "SUBMIT & DONE", "Submit and start connecting."]].map(([Icon, t, d], i) => (
              <div key={t} className="card-surface p-4 text-center relative" data-testid={`register-how-${i + 1}`}>
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-techblue text-white font-mono text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
                <Icon size={18} className="text-techblue-light mx-auto mt-2 mb-2" />
                <p className="text-[10.5px] font-bold tracking-wide text-slate-100">{t}</p>
                <p className="text-[10px] text-slate-500 mt-1 leading-snug">{d}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="card-surface hairline-gold p-6 mt-8 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left" data-testid="scan-cta-card">
          <Sparkles size={22} className="text-gold shrink-0" />
          <div className="flex-1">
            <p className="font-display font-bold text-slate-100 text-sm">Have a visiting card? Skip the typing.</p>
            <p className="text-[11px] text-slate-500 mt-1">Scan it and we auto-fill your registration — review & submit in seconds.</p>
          </div>
          <Link to="/scan" data-testid="scan-register-btn" className="btn-outline shrink-0 !px-5 !py-2.5 text-xs">SCAN CARD & REGISTER</Link>
        </div>

        <div className="flex flex-wrap justify-center gap-x-10 gap-y-3 mt-10" data-testid="register-trust-strip">
          {[["Secure & Trusted", ShieldCheck], ["AI-Built Profile", Zap], ["Verified Network", BadgeCheck]].map(([t, Icon]) => (
            <div key={t} className="flex items-center gap-2 text-slate-500">
              <Icon size={15} className="text-gold" />
              <span className="text-[11px] font-medium">{t}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
